import React from 'react';

import ImageBase from './ImageBase';
import { Icon } from '../Icon';

type TAvatar = {
  src: string;
  alt: string;
  size?: 's' | 'm' | 'l';
  className?: string;
  placeholderIconName: string;
};

function shouldRenderPlaceholder(src: string, placeholderIconName: string) {
  return !Boolean(src) && Boolean(placeholderIconName);
}

function getWidthHeight(size: TAvatar['size'] = 'm') {
  const sizes = {
    s: '32px',
    m: '48px',
    l: '64px',
  };

  return { width: sizes[size], height: sizes[size] };
}

function createAvatarClassName(shouldShowPlaceholderIcon: boolean, className: string | undefined) {
  const placeholderClass = shouldShowPlaceholderIcon ? 'hz-Image--avatar-placeholder' : '';
  return [placeholderClass, className].join(' ').trim();
}

type TGetAvatarProps = {
  alt: string;
  size: TAvatar['size'];
  className?: string;
  shouldShowPlaceholderIcon: boolean;
  rest: Record<string, unknown>;
};

function getAvatarProps({ alt, size, className, shouldShowPlaceholderIcon, rest }: TGetAvatarProps) {
  const as: keyof React.JSX.IntrinsicElements = shouldShowPlaceholderIcon ? 'div' : 'img';

  return {
    ...rest,
    as,
    kind: 'avatar' as keyof typeof ImageBase,
    alt,
    className: createAvatarClassName(shouldShowPlaceholderIcon, className),
    style: { ...getWidthHeight(size) },
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Avatar = ({ src, alt, size = 'm', className, placeholderIconName, ...rest }: TAvatar) => {
  const shouldShowPlaceholderIcon = shouldRenderPlaceholder(src, placeholderIconName);
  const _props = getAvatarProps({
    shouldShowPlaceholderIcon,
    rest,
    alt,
    className,
    size,
  });

  if (shouldShowPlaceholderIcon) {
    return (
      <ImageBase {..._props}>
        <Icon name={placeholderIconName} color="background-default" className="hz-Image--placeholder-icon" />
      </ImageBase>
    );
  }

  return <ImageBase {..._props} src={src} />;
};

export default Avatar;
